import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import styled, { keyframes, css } from "styled-components";

/* ─── Animations ─────────────────────────────────────────────────────────── */
const slideIn  = keyframes`from{opacity:0;transform:translateX(100%) scale(.95)}to{opacity:1;transform:translateX(0) scale(1)}`;
const slideOut = keyframes`from{opacity:1;transform:translateX(0) scale(1)}to{opacity:0;transform:translateX(110%) scale(.95)}`;
const progress = keyframes`from{width:100%}to{width:0%}`;

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const Container = styled.div`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  max-width: 360px;
  width: calc(100vw - 2rem);
  pointer-events: none;
`;

const Toast = styled.div`
  background: ${({ theme }) => theme.bgCard};
  border: 1px solid ${({ $type, theme }) =>
    $type === "success" ? theme.green + "55"  :
    $type === "error"   ? theme.red  + "55"   :
    $type === "warning" ? theme.gold + "55"   : theme.borderLight};
  border-radius: 12px;
  padding: 0.85rem 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  pointer-events: all;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5);
  animation: ${({ $exiting }) => $exiting
    ? css`${slideOut} 0.25s ease forwards`
    : css`${slideIn} 0.25s ease`};
`;

const IconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
  background: ${({ $type, theme }) =>
    $type === "success" ? theme.greenDim :
    $type === "error"   ? theme.redDim   :
    $type === "warning" ? "rgba(255,193,7,.12)" : theme.bgHover};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToastTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  line-height: 1.3;
`;

const ToastMsg = styled.div`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.textDim};
  margin-top: 0.15rem;
  line-height: 1.4;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.textMuted};
  cursor: pointer;
  font-size: 0.9rem;
  line-height: 1;
  padding: 0;
  flex-shrink: 0;
  &:hover { color: ${({ theme }) => theme.text}; }
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: ${({ $type, theme }) =>
    $type === "success" ? theme.green :
    $type === "error"   ? theme.red   :
    $type === "warning" ? theme.gold  : theme.accent};
  animation: ${progress} ${({ $duration }) => $duration}ms linear forwards;
`;

/* ─── Constants ──────────────────────────────────────────────────────────── */
const ICONS = {
  success: "✓",
  error:   "✕",
  warning: "⚠",
  info:    "ℹ",
};

const DEFAULT_DURATION = 4000;

/* ─── Context ────────────────────────────────────────────────────────────── */
const ToastContext = createContext(null);

let _id = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id]);
    // Mark as exiting for animation, then remove
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 260);
  }, []);

  const push = useCallback(({ type = "info", title, message, duration = DEFAULT_DURATION }) => {
    const id = ++_id;
    setToasts(prev => [...prev.slice(-4), { id, type, title, message, duration, exiting: false }]);
    timers.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  /* Convenience helpers */
  const toast = {
    success: (title, message, opts) => push({ type: "success", title, message, ...opts }),
    error:   (title, message, opts) => push({ type: "error",   title, message, ...opts }),
    warning: (title, message, opts) => push({ type: "warning", title, message, ...opts }),
    info:    (title, message, opts) => push({ type: "info",    title, message, ...opts }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <Container>
        {toasts.map(t => (
          <Toast key={t.id} $type={t.type} $exiting={t.exiting}>
            <IconWrap $type={t.type}>{ICONS[t.type]}</IconWrap>
            <Body>
              {t.title   && <ToastTitle>{t.title}</ToastTitle>}
              {t.message && <ToastMsg>{t.message}</ToastMsg>}
            </Body>
            <CloseBtn onClick={() => dismiss(t.id)}>✕</CloseBtn>
            <ProgressBar $type={t.type} $duration={t.duration} />
          </Toast>
        ))}
      </Container>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
